<template>
  <div>
    <div class="map-sidebar-overlay d-flex flex-row">
      <div class="map-sidebar flex-grow-1">
        <div class="px-4 mt-2">
          <div class="text-h6 d-flex justify-space-between">
            View Monitor
            <v-btn
              v-if="isAdminOrSuperAdmin"
              id="edit-button"
              class="mt-1"
              @click="disable_monitor()"
              :disabled="loading || !monitor.active"
              small
              outlined
            >
              Disable
            </v-btn>
          </div>
        </div>

        <!--
        height calculation below allows us to keep the title
        and reset/create footer buttons sticky while providing
        a scroll bar if the panels expanded are greater than
        the available height

        64px = app header height
        48px = title height + 8px top margin
        68px = view all tiles/view cart footer height
        20px = padding for top and bottom
      -->
        <div
          style="
            max-height: calc(100vh - (64px + 48px + 68px + 20px));
            overflow: auto;
          "
          class="px-4"
        >
          <v-list dense v-if="this.monitor_id || this.monitor_name">
            <v-list-item v-if="this.monitor_name" class="px-6"
              ><span class="pr-3 text--secondary">{{ "Name: " }}</span
              >{{ this.monitor_name }}</v-list-item
            >
            <v-list-item v-else class="px-6">
              <span class="pr-3 text--secondary">{{ "Monitor ID: " }}</span>
              {{ this.monitor_id }}</v-list-item
            >
          </v-list>
          <v-expansion-panels v-model="panel" accordion focusable multiple tile>
            <v-expansion-panel id="info-panel">
              <v-expansion-panel-header>Monitor Info</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-responsive
                  class="overflow-y-auto"
                  max-height="400"
                  v-if="Object.keys(monitor).length > 0"
                >
                  <v-list dense>
                    <v-list-item v-if="this.monitor_name"
                      ><span class="pr-3 text--secondary">{{
                        "Monitor ID: "
                      }}</span>
                      {{ this.monitor_id }}</v-list-item
                    >
                    <v-list-item
                      ><span class="pr-3 text--secondary">{{
                        "Created on: "
                      }}</span
                      >{{
                        new Date(monitor.created).toLocaleString()
                      }}</v-list-item
                    >
                    <v-list-item
                      ><span class="pr-3 text--secondary">{{
                        "Active: "
                      }}</span>
                      {{ monitor.active }}</v-list-item
                    >
                    <v-list-item v-if="monitor.description"
                      ><span class="pr-3 text--secondary">{{
                        "Description: "
                      }}</span
                      >{{ monitor.description }}</v-list-item
                    >
                  </v-list>
                </v-responsive>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel id="view-filters-panel">
              <v-expansion-panel-header
                >Monitor Parameters
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <view-filters
                  :filters="this.monitor"
                  v-if="Object.keys(monitor).length > 0"
                ></view-filters>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel id="view-match-history-panel">
              <v-expansion-panel-header
                >Match History
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <match-history-table
                  :monitor_history="monitor_history"
                  :has_more="has_more"
                  :page_token="page_token"
                  :loading="history_loading"
                  :match_loading="match_loading"
                  @view_match="view_match($event)"
                  @remove_match="remove_match()"
                ></match-history-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <div class="pa-4 d-flex justify-space-between highlight">
          <AllTiles
            v-if="formated.length > 0"
            ref="all_tiles"
            :selectResult="formated"
            :cell_features="this.select_features"
            @view_cart="$refs.shopping_cart.dialog = true"
          />
          <shopping-cart
            v-if="formated.length > 0"
            ref="shopping_cart"
            :selectResult="formated"
            :select_id="select_id"
            @view_all_tiles="$refs.all_tiles.dialog = true"
          />
        </div>
      </div>
    </div>
    <div v-show="loading" class="map-padded-overlay">
      <div class="text-center pa-2">
        <spinner>{{ this.loading_message }}...</spinner>
      </div>
    </div>
    <div
      class="bottom-tile-viewer"
      v-if="map_loaded && !loading && get_cell_feature(cell_id)"
    >
      <bottom-tile-viewer
        id="row-tiles"
        :cell_feature="get_cell_feature(cell_id)"
        :hideTabs="true"
      />
    </div>
    <confirm-modal
      ref="confirmDisableModal"
      title="Disable?"
      confirm-btn-text="Yes, Disable"
      cancel-btn-text="No, Cancel"
    >
      <div style="max-width: 350px">
        Disabling a monitor is permanent and cannot be reenabled. Monitor
        details and history can still be viewed. Are you sure you want to
        continue?
      </div>
    </confirm-modal>
  </div>
</template>

<script>
import Spinner from "@/components/shared/Spinner";
import turfBbox from "@turf/bbox";
import turfBboxPolygon from "@turf/bbox-polygon";
import Wkt from "wicket";
import selectApi from "@/api/select";
import monitorApi from "@/api/monitor";
import { createNamespacedHelpers } from "vuex-composition-helpers";
// import MonitorParams from "@/components/monitor/MonitorParams.vue";
import MatchHistoryTable from "@/components/monitor/MatchHistoryTable.vue";
import ViewFilters from "@/components/ViewFilters.vue";
import ShoppingCart from "@/components/select/ShoppingCart.vue";
import BottomTileViewer from "@/components/select/BottomTileViewer.vue";
import AllTiles from "@/components/select/AllTiles.vue";
import ConfirmModal from "@/components/shared/ConfirmModal";
import mapUtils from "@/utils/map";
import userUtils from "@/utils/userType";
import { mapGetters } from "vuex";
import _ from "lodash";

const uiStore = createNamespacedHelpers("ui");
const cartStore = createNamespacedHelpers("shoppingCart");

export default {
  props: {
    map: {
      type: Object,
      required: true,
    },
    map_loaded: {
      type: Boolean,
      required: true,
    },
    drawnFeature: {
      type: Object,
      default: null,
    },
  },
  mixins: [mapUtils],
  components: {
    Spinner,
    ViewFilters,
    ShoppingCart,
    BottomTileViewer,
    AllTiles,
    MatchHistoryTable,
    ConfirmModal,
  },

  data() {
    const empty_features = {
      type: "FeatureCollection",
      features: [],
    };

    return {
      loading: false,
      history_loading: false,
      match_loading: false,
      monitor_name: null,
      monitor: {},
      monitor_id: "",
      monitor_history: [],
      previewImageSourceNames: [],
      has_more: false,
      page_token: null,
      select_id: "",
      cell_id: null,
      selected_cell_metadata: null,
      panel: [2],
      metadataSourceName: "Select Tiles",
      drawnPolygonSourceName: "Select AOI",
      cellTextLayerName: "Cell Text Values",
      select_aoi: [],
      select_features: [],
      cell_count: 0,
      tile_count: 0,
      unique_acquisitions: [],
      stack_depth: 5,
      textPropertyName: "available_tiles",
      formated: [],
      mounted: false,
      loading_message: "Loading Monitor",
      select_tiles: [],
      empty_features,
      covering_cells_features: [],
    };
  },
  computed: {
    ...mapGetters(["user_details"]),
    isAdminOrSuperAdmin() {
      return this.user_details && userUtils.is_admin(this.user_details);
    },
  },
  setup() {
    const { addToCart } = cartStore.useMutations(["addToCart"]);
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR, addToCart };
  },
  watch: {
    $route() {
      this.get_monitor_history();
    },
  },
  mounted() {
    this.mounted = true;
    this.initMap();
  },
  beforeDestroy() {
    // remove everything we have drawn
    this.removePreviewImages(this);
    const sourcesToRemove = [
      {
        name: this.metadataSourceName,
        layersToRemove: [
          this.metadataSourceName,
          `${this.metadataSourceName} Outline`,
          this.cellTextLayerName,
        ],
      },
      {
        name: this.drawnPolygonSourceName,
        layersToRemove: [
          `${this.drawnPolygonSourceName} Fill`,
          `${this.drawnPolygonSourceName} Outline`,
        ],
      },
      { name: "cell-extent", layersToRemove: ["hightlight-cell-bounds"] },
      { name: "hover-extent", layersToRemove: ["hover-cell-bounds"] },
    ];
    sourcesToRemove.forEach((s) => {
      const source = this.map.getSource(s.name);

      // only remove the source and layers if there is a source
      if (source) {
        this.map.removeSourceLayers(s.name, s.layersToRemove);

        // null out data
        source.setData({ type: "FeatureCollection", features: [] });
      }
    }, this);
  },
  methods: {
    initMap() {
      if (this.map_loaded) {
        this.loading = true;
        this.history_loading = true;

        // eslint-disable-next-line
        const me = this;
        const map = this.map;

        // eslint-disable-next-line
        const mapboxgl = require("mapbox-gl");
        const metadataOutlineSourceName = `${this.metadataSourceName} Outline`;
        const drawnFillLayerId = `${this.drawnPolygonSourceName} Fill`;

        map.addSourceLayers(
          this.drawnPolygonSourceName,
          {
            type: "geojson",
            data: null,
          },
          [
            {
              id: drawnFillLayerId,
              type: "fill",
              source: this.drawnPolygonSourceName,
              paint: {
                "fill-color": "#FFFF00",
                "fill-outline-color": "#FFFF00",
                "fill-opacity": 0.05,
              },
            },
            {
              id: `${this.drawnPolygonSourceName} Outline`,
              type: "line",
              source: this.drawnPolygonSourceName,
              layout: {},
              paint: {
                "line-color": "#FFFF00",
                "line-width": 3,
              },
            },
          ],
          [
            // Handle click event on a grid item
            {
              type: "click",
              layerId: this.metadataSourceName,
              handler: (e) => {
                const map = e.target;
                map.getCanvas().style.cursor = "";
                if (map.getSource("cell-extent")) {
                  map.getSource("cell-extent").setData(e.features[0]);
                } else {
                  map.addSource("cell-extent", {
                    type: "geojson",
                    data: e.features[0],
                  });
                  map.addLayer({
                    id: "hightlight-cell-bounds",
                    type: "line",
                    source: "cell-extent",
                    layout: {},
                    paint: {
                      "line-width": 5,
                      "line-color": "#FCAF17", // maxar primary gold color
                    },
                  });
                }
                this.cell_id = e.features[0].properties.id;
                this.panel = [2];
              },
            },
            // Highlight grid on hover as well as change the cursor to a pointer
            {
              type: "mousemove",
              layerId: this.metadataSourceName,
              handler: (e) => {
                const map = e.target;

                if (me.hoveredSquareId) {
                  map.setFeatureState(
                    { source: me.metadataSourceName, id: me.hoveredSquareId },
                    { hover: false }
                  );
                }

                if (!isNaN(e.features[0].id)) {
                  map.getCanvas().style.cursor = "pointer";

                  me.hoveredSquareId = e.features[0].id;
                  map.setFeatureState(
                    { source: me.metadataSourceName, id: me.hoveredSquareId },
                    { hover: true }
                  );

                  if (map.getSource("hover-extent")) {
                    map.getSource("hover-extent").setData(e.features[0]);
                  } else {
                    map.addSource("hover-extent", {
                      type: "geojson",
                      data: e.features[0],
                    });
                    map.addLayer({
                      id: "hover-cell-bounds",
                      type: "line",
                      source: "hover-extent",
                      layout: {},
                      paint: {
                        "line-width": 3,
                        "line-color": "#026AE3", // maxar secondary link color
                      },
                    });
                  }
                } else {
                  map.getCanvas().style.cursor = "";

                  if (map.getSource("hover-extent")) {
                    map.getSource("hover-extent").setData(this.empty_features);
                  }
                }
              },
            },
            // Remove highlight when we leave a grid item as well as clear the cursor style
            {
              type: "mouseleave",
              layerId: this.metadataSourceName,
              handler: (e) => {
                const map = e.target;
                map.getCanvas().style.cursor = "";

                if (me.hoveredSquareId) {
                  map.setFeatureState(
                    { source: me.metadataSourceName, id: me.hoveredSquareId },
                    { hover: false }
                  );
                }

                if (map.getSource("hover-extent")) {
                  map.getSource("hover-extent").setData(this.empty_features);
                }

                me.hoveredSquareId = null;
              },
            },
          ]
        );

        map.addSourceLayers(
          this.metadataSourceName,
          {
            type: "geojson",
            data: null,
          },
          [
            {
              id: this.metadataSourceName,
              type: "fill",
              source: this.metadataSourceName,
              paint: {
                // fully transparent but clickable
                "fill-color": "rgba(0, 0, 0, 0.0)",
              },
            },
            {
              id: metadataOutlineSourceName,
              type: "line",
              source: this.metadataSourceName,
              layout: {},
              paint: {
                "line-color": "rgba(204, 204, 204, 0.8)", // default to 80% opaque gray
                "line-width": 3,
              },
            },
          ]
        );

        this.get_monitor();
      } else {
        setTimeout(this.initMap, 200);
      }
    },

    parseGeom(geom) {
      if (typeof geom === "string") {
        var wkt = new Wkt.Wkt();
        wkt.read(geom);
        geom = wkt.toJson();
      }
      return geom;
    },

    getPolygon(aoi) {
      return [
        {
          type: "Feature",
          geometry: this.parseGeom(aoi),
        },
      ];
    },

    get_cell_feature(cell_id) {
      function get_feature(feature) {
        if (feature.properties.id === cell_id) {
          return feature;
        }
      }
      if (this.select_features.constructor === Object) {
        var cell_feature = null;
        cell_feature = this.select_features.features.filter(get_feature);
        return cell_feature[0];
      } else {
        return null;
      }
    },

    load_select_aoi(select_request) {
      if (select_request.intersects) {
        this.select_aoi = this.getPolygon(select_request.intersects);
      } else if (select_request.bbox) {
        const bboxPolygon = turfBboxPolygon(select_request.bbox);
        this.select_aoi = this.getPolygon(bboxPolygon);
        // geometry needs to be unnested for bboxes
        this.select_aoi[0].geometry = this.select_aoi[0].geometry.geometry;
      }

      this.map
        .getSource(this.drawnPolygonSourceName)
        .setData(this.select_aoi[0]);
    },

    async view_match(event) {
      this.$log.debug(`viewing match with query_id=${event.query_id}...`);
      this.match_loading = true;
      this.formated = [];
      this.removePreviewImages(this);
      if (this.map.getSource("cell-extent")) {
        this.map.getSource("cell-extent").setData(this.empty_features);
      }
      this.loading = true;
      this.loading_message = "Loading Match";
      this.select_loading_messages();

      try {
        const info_response = await selectApi.get_select_info(event.query_id);
        this.$log.debug(
          `Retrieved select info=${JSON.stringify(info_response)}`
        );
        this.$log.debug(`Re-running select...`);

        try {
          const rerun_response = await selectApi.rerun_select(
            event.query_id,
            "stac"
          );

          this.select_tiles = JSON.parse(JSON.stringify(rerun_response.data));
          await this.load_select_features(
            this,
            info_response.data.tile_query.criteria,
            rerun_response.data
          );

          const current_acquisition_tiles = {
            type: "FeatureCollection",
            features: [],
          };

          // collections are the cells, bouding boxes aren't good for mapping the browse images
          // features are tiles

          // for each feature use the cell geometries instead of the tile geometries
          this.select_tiles.features.forEach((f) => {
            const cell_id = f.properties.cell_id;

            this.covering_cells_features.every((c) => {
              this.$log.debug(
                `does select tile cell_id=${cell_id} match covering cell id=${c.properties.id}?`
              );

              if (c.properties.id === cell_id && c.geometry) {
                this.$log.debug(`found MATCH!`);
                current_acquisition_tiles.features.push({
                  ...f,
                  geometry: c.geometry,
                });

                return false;
              }

              return true;
            }, this);
          });

          this.current_acquisition_tiles = current_acquisition_tiles;
          this.previewImageSourceNames = this.addPreviewImages(
            current_acquisition_tiles.features,
            this.map,
            [
              `${this.metadataSourceName} Outline`,
              `${this.drawnPolygonSourceName} Fill`,
              this.metadataSourceName,
              `${this.drawnPolygonSourceName} Outline`,
              "hover-cell-bounds",
              "hightlight-cell-bounds",
            ],
            this.timelineMetadataSourceName
          );

          this.panel = [2];
        } catch (err) {
          this.$log.error(
            `Failed to load select features with criteria=${JSON.stringify(
              info_response.data.tile_query.criteria
            )}, error=${err.toString()}`
          );
        }
      } catch (err) {
        this.$log.error(
          `Failed to get select info to view match=${
            event.query_id
          }, error=${err.toString()}`
        );
      } finally {
        this.loading = false;
        this.match_loading = false;
      }
    },

    remove_match() {
      this.match_loading = true;
      this.select_features = this.empty_features;
      this.formated = [];
      this.map.getSource(this.metadataSourceName).setData(this.select_features);
      if (this.map.getSource("cell-extent")) {
        this.map.getSource("cell-extent").setData(this.empty_features);
      }
      this.removePreviewImages(this);
      this.match_loading = false;
    },

    get_monitor() {
      monitorApi
        .get_monitor(this.$route.params.monitor_id)
        .then((response) => {
          this.monitor_name = response.data.monitor.name;
          this.monitor_id = response.data.monitor.monitor_id;
          this.monitor = response.data.monitor;
          this.loading = false;

          this.load_select_aoi(response.data.monitor);
          this.panMap(this.map, turfBbox(this.select_aoi[0]));

          this.get_monitor_history();
        })
        .catch(() => {
          // nothing
        });
    },

    get_monitor_history() {
      this.history_loading = true;
      const monitor_id = this.$route.params.monitor_id;
      const page_token = this.$route.query.paging_id || "";
      const sort = this.$route.query.sort || "desc";
      monitorApi
        .get_monitor_history(monitor_id, page_token, sort)
        .then((response) => {
          this.monitor_history = response.data.data;
          this.has_more = response.data.has_more;
          this.page_token = response.data.page_token || null;
          this.history_loading = false;
        })
        .catch(() => {
          // nothing
        });
    },

    disable_monitor() {
      this.$refs.confirmDisableModal.show().then((yes) => {
        if (yes) {
          this.loading_message = "Disabling Monitor";
          this.loading = true;
          monitorApi
            .disable_monitor(this.$route.params.monitor_id)
            .then(() => {
              this.get_monitor();
            })
            .catch(() => {
              // nothing
            });
        }
      });
    },

    select_loading_messages() {
      let delay = 1000;
      for (let count = 0; count < 10; count++) {
        setTimeout(() => {
          if (
            this.loading_message.startsWith("Loading Selection") ||
            this.loading_message.startsWith("Gathering matching images")
          ) {
            this.loading_message = `Gathering matching images (${count}/10)`;
          }
        }, delay);
        delay += 2000;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.map-bottom-controls {
  position: absolute;
  right: 5px;
  bottom: 100px;
  width: 25%;
  max-width: 350px;
}

.bottom-tile-viewer {
  position: absolute;
  left: -140px;
  bottom: 50px;
  width: 25%;
  max-width: 350px;
  z-index: 4;
}
</style>
